// Welcome Screen Component
import { useState } from "react";
import { Bot, Plus, Sparkles, MessageSquare, Image, FileText, Globe } from "lucide-react";
import { useLanguage } from "@/i18n";

// Desteklenen diller
const LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' }
];

// Male icon SVG
const MaleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="7" r="4"/>
    <path d="M5.5 21a8.38 8.38 0 0 1 13 0"/>
    <path d="M12 11v4"/>
  </svg>
);

// Female icon SVG
const FemaleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="7" r="4"/>
    <path d="M5.5 21a8.38 8.38 0 0 1 13 0"/>
    <path d="M9 11c0 1.5 1.5 3 3 3s3-1.5 3-3"/>
  </svg>
);

export function WelcomeScreen({
  user,
  selectedAssistant,
  selectedCustomAssistant,
  customAssistants,
  anonymousLimit,
  selectDefaultAssistant,
  selectCustomAssistant,
  createNewConversation,
  setShowAssistantCreator,
  setShowAuthModal,
  setAuthMode,
  mode,
  setMode
}) {
  const { t, language, setLanguage } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);
  
  return (
    <div className="welcome-screen" data-testid="welcome-screen">
      {/* Dil Seçici - Sabit Sağ Üst Köşe (tıkla-aç: dokunmatik cihazlarda da çalışır) */}
      <div className="fixed top-4 right-4 z-50">
        <div className="relative">
          <button 
            onClick={() => setLangOpen((v) => !v)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:border-blue-500 hover:text-blue-400 transition-all shadow-lg"
            data-testid="language-selector"
          >
            <Globe size={16} />
            <span>{LANGUAGES.find(l => l.code === language)?.flag || '🌐'}</span>
            <span className="text-sm hidden sm:inline">{LANGUAGES.find(l => l.code === language)?.name || language}</span>
          </button>
          
          {/* Dropdown */}
          {langOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} data-testid="lang-overlay" />
            <div className="absolute right-0 top-full mt-2 w-48 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--bg-hover)] transition-colors first:rounded-t-lg last:rounded-b-lg ${
                    language === lang.code ? 'bg-blue-600/20 text-blue-400' : 'text-[var(--text-secondary)]'
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="text-sm">{lang.name}</span>
                </button>
              ))}
            </div>
          </>
          )}
        </div>
      </div>

      <div className="welcome-logo"><Bot size={48} color="white" /></div>
      <h1 className="welcome-title text-[var(--text-primary)]">{user ? t('welcome_user', { name: user.name }) : t('welcome')}</h1>
      <p className="welcome-subtitle text-[var(--text-secondary)]">{t('select_assistant')}</p>
      
      {/* Asistan Seçimi */}
      <div className="mt-6 flex flex-col items-center gap-4">
        <div className="flex gap-3 flex-wrap justify-center">
          {/* Kaan */}
          <button
            onClick={() => { selectDefaultAssistant("kaan"); createNewConversation(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
              selectedAssistant === "kaan" && !selectedCustomAssistant
                ? "bg-blue-600 border-blue-500 text-white" 
                : "bg-[var(--bg-tertiary)] border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]"
            }`}
            data-testid="assistant-kaan-btn"
          >
            <MaleIcon />
            <span className="font-medium">Kaan</span>
          </button>
          
          {/* Defne */}
          <button
            onClick={() => { selectDefaultAssistant("defne"); createNewConversation(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
              selectedAssistant === "defne" && !selectedCustomAssistant
                ? "bg-pink-600 border-pink-500 text-white" 
                : "bg-[var(--bg-tertiary)] border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]"
            }`}
            data-testid="assistant-defne-btn"
          >
            <FemaleIcon />
            <span className="font-medium">Defne</span>
          </button>
          
          {/* Özel Asistanlar */}
          {customAssistants.map((ca) => (
            <button
              key={ca.id}
              onClick={() => { selectCustomAssistant(ca); createNewConversation(); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                selectedCustomAssistant?.id === ca.id
                  ? "bg-purple-600 border-purple-500 text-white" 
                  : "bg-[var(--bg-tertiary)] border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]"
              }`}
              data-testid={`assistant-custom-${ca.id}`}
            >
              <Sparkles size={18} />
              <span className="font-medium">{ca.name}</span>
            </button>
          ))}
          
          {/* Yeni Asistan Oluştur Butonu */}
          {user && (
            <button
              onClick={() => setShowAssistantCreator(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-[var(--border-color)] text-[var(--text-muted)] hover:border-purple-500 hover:text-purple-400 transition-all"
              data-testid="create-assistant-btn"
            >
              <Plus size={18} />
              <span className="font-medium">{t('new_assistant')}</span>
            </button>
          )}
        </div>
        
        {/* Asistan Açıklaması */}
        <div className="text-center max-w-md">
          {selectedCustomAssistant ? (
            <p className="text-[var(--text-secondary)] text-sm">
              <span className="text-purple-400 font-medium">{selectedCustomAssistant.name}</span> - {selectedCustomAssistant.description}
            </p>
          ) : selectedAssistant === "defne" ? (
            <p className="text-[var(--text-secondary)] text-sm">
              <span className="text-pink-400 font-medium">{t('assistant_defne')}</span> - {t('assistant_defne_desc')}
            </p>
          ) : (
            <p className="text-[var(--text-secondary)] text-sm">
              <span className="text-blue-400 font-medium">{t('assistant_kaan')}</span> - {t('assistant_kaan_desc')}
            </p>
          )}
        </div>
        
        {/* Mode Seçimi - Sohbet/Görsel/Dosya */}
        <div className="mode-tabs mt-4">
          <button className={`mode-tab ${mode === "chat" ? "active" : ""}`} onClick={() => setMode("chat")} data-testid="mode-chat">
            <MessageSquare size={16} /> {t('mode_chat') || 'Sohbet'}
          </button>
          <button className={`mode-tab ${mode === "image" ? "active" : ""}`} onClick={() => setMode("image")} data-testid="mode-image">
            <Image size={16} /> {t('mode_image') || 'Görsel'}
          </button>
          <button className={`mode-tab ${mode === "file" ? "active" : ""}`} onClick={() => setMode("file")} data-testid="mode-file">
            <FileText size={16} /> {t('mode_file') || 'Dosya'}
          </button>
        </div>
        
        {!user && (
          <div className="flex flex-col items-center gap-2">
            {anonymousLimit.remaining > 0 ? (
              <>
                <div className="flex items-center gap-2 bg-[var(--bg-tertiary)]/50 px-4 py-2 rounded-lg">
                  <span className="text-[var(--text-secondary)] text-sm">{t('remaining_free')}:</span>
                  <span className={`font-bold ${anonymousLimit.remaining > 2 ? 'text-green-400' : anonymousLimit.remaining > 1 ? 'text-yellow-400' : 'text-orange-400'}`}>
                    {anonymousLimit.remaining}/{anonymousLimit.limit}
                  </span>
                </div>
                <p className="text-[var(--text-muted)] text-sm">
                  {t('unlimited_access')} <button onClick={() => setShowAuthModal(true)} className="text-blue-400 hover:underline">{t('login')}</button> {t('or')} <button onClick={() => { setAuthMode("register"); setShowAuthModal(true); }} className="text-blue-400 hover:underline">{t('register')}</button>
                </p>
              </>
            ) : (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <p className="text-red-400 font-medium mb-2">{t('daily_limit_reached')}</p>
                <p className="text-[var(--text-secondary)] text-sm mb-3">{t('create_account')}</p>
                <div className="flex gap-2 justify-center">
                  <button onClick={() => setShowAuthModal(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors">{t('login')}</button>
                  <button onClick={() => { setAuthMode("register"); setShowAuthModal(true); }} className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition-colors">{t('register')}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
